import { useEffect } from 'react';
import { useGetReferralNameByCodeQuery } from '../store';
import {
  clearStoredReferral,
  readStoredReferral,
  writeStoredReferral,
} from '../utils/eventReferral';
import useQuery from './useQuery';

interface EventReferralCode {
  // Valid for this event only. Null if missing, still loading, or the code
  // belongs to another event (name lookup returned null).
  code: string | null;
  name: string | null;
  showHeadsUp: boolean;
  isResolving: boolean;
}

const useEventReferralCode = (eventId?: string | null): EventReferralCode => {
  const query = useQuery();
  const fromQuery = query.get('ref')?.trim() || null;
  const fromStorage = eventId ? readStoredReferral(eventId) : null;
  const candidate = fromQuery || fromStorage;

  useEffect(() => {
    if (eventId && fromQuery) {
      writeStoredReferral(eventId, fromQuery);
    }
  }, [eventId, fromQuery]);

  const {
    data, isLoading, isFetching, isError,
  } = useGetReferralNameByCodeQuery(
    { code: candidate || '', eventId: eventId || '' },
    { skip: !candidate || !eventId },
  );

  useEffect(() => {
    if (!eventId || !candidate || isLoading || isFetching || isError) {
      return;
    }
    if (!data?.name) {
      clearStoredReferral(eventId);
    }
  }, [eventId, candidate, data?.name, isLoading, isFetching, isError]);

  const isResolving = Boolean(candidate && eventId && (isLoading || isFetching));
  const isValid = Boolean(data?.name);

  return {
    // On a failed lookup keep the candidate: the backend drops an invalid
    // code and a network blip must not silently un-attribute a real seller.
    code: isValid || isError ? candidate : null,
    name: data?.name ?? null,
    showHeadsUp: isValid && (data?.show_heads_up ?? false),
    isResolving,
  };
};

export default useEventReferralCode;
